import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export function useAddToCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ collectionId, promptId }: { collectionId: string; promptId: string }) => {
      const { error } = await supabase
        .from("collection_prompts")
        .insert({
          collection_id: collectionId,
          prompt_id: promptId,
        });

      if (error) {
        if (error.code === "23505") {
          throw new Error("Prompt already in this collection");
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
      queryClient.invalidateQueries({ queryKey: ["collection-prompts"] });
      toast.success("Added to collection!");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to add to collection");
    },
  });
}

export function useRemoveFromCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ collectionId, promptId }: { collectionId: string; promptId: string }) => {
      const { error } = await supabase
        .from("collection_prompts")
        .delete()
        .eq("collection_id", collectionId)
        .eq("prompt_id", promptId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
      queryClient.invalidateQueries({ queryKey: ["collection-prompts"] });
      toast.success("Removed from collection");
    },
    onError: () => {
      toast.error("Failed to remove from collection");
    },
  });
}

export function usePromptCollections(promptId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["prompt-collections", promptId, user?.id],
    queryFn: async () => {
      if (!user || !promptId) return [];

      const { data, error } = await supabase
        .from("collection_prompts")
        .select(`
          collection_id,
          prompt_collections!inner(id, name, emoji, user_id)
        `)
        .eq("prompt_id", promptId)
        .eq("prompt_collections.user_id", user.id);

      if (error) throw error;

      return (data || []).map((item: any) => ({
        id: item.prompt_collections.id,
        name: item.prompt_collections.name,
        emoji: item.prompt_collections.emoji,
      }));
    },
    enabled: !!user && !!promptId,
  });
}

export function useUpdatePromptOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ collectionId, promptId, newOrder }: { collectionId: string; promptId: string; newOrder: number }) => {
      const { error } = await supabase
        .from("collection_prompts")
        .update({ display_order: newOrder })
        .eq("collection_id", collectionId)
        .eq("prompt_id", promptId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collection-prompts"] });
    },
  });
}
